<template>
  <div class="card p-2">
    <div class="is-flex is-flex-direction-row">
      <div class="is-flex-grow-5">
        <h3 class="is-size-5 has-text-bold"></h3>
      </div>
      <button
        type="button"
        aria-label="Close message"
        class="delete is-flex-grow-0"
        @click="reject()"
      ></button>
    </div>

    <div class="p-4">
      <div v-if="!loading && !error">
        <div class="columns is-mobile">
          <!-- AIRLINE LOGO -->
          <div class="column is-narrow has-text-centered wrap">
            <div class="mt-1">
              <img
                width="30"
                :src="$helpers.airlineLogo(flight.airlineCode, 30, 30)"
              />
            </div>
          </div>
          <!-- FROM-TO -->
          <div class="column">
            <div class="has-text-weight-semibold">
              {{ $helpers.airlineName(flight.airlineCode) }}
            </div>
            <div class="columns">
              <JourneySummaryAlt
                v-for="(journey, journeyIndex) in flight.journeys"
                :key="journeyIndex"
                :journey="journey"
                :index="journeyIndex"
                class="column is-narrow"
              />
            </div>
          </div>
        </div>

        <NDCTag class="mt-2"></NDCTag>
        <h3 class="is-size-4 has-text-bold mr-2">
          {{ fareDescription }}
        </h3>
        <div class="is-flex is-align-items-center">
          <div class="is-size-6 has-text-grey mr-2">
            Current price:
          </div>
          <div class="is-size-5 has-text-grey mr-2">
            {{ pricingInfo.total | formatCurrency(2, pricingInfo.currency) }}
          </div>
          <div
            v-if="priceComparisonDifference !== 0"
            class="is-flex is-align-items-center"
          >
            <b-icon
              :icon="priceComparisonIcon"
              :type="priceComparisonType"
              size="is-small"
            ></b-icon>
            <span
              :class="
                `is-size-7 ${
                  priceComparisonType === 'is-success'
                    ? 'has-text-success'
                    : 'has-text-danger'
                }`
              "
            >
              {{ Math.abs(priceComparisonDifference) | formatCurrency(2, pricingInfo.currency) }}
            </span>
          </div>
        </div>

        <!-- Pricing Breakdown -->
        <div v-if="pricingInfo" class="has-background-light my-4 px-4 py-2">
          <div class="columns mt-0 mb-0 is-size-7 has-text-weight-bold">
            <div class="column p-0 has-text-left ">Selected Fare</div>
            <div class="column p-0 has-text-right is-narrow">
              {{ pricingInfo.total | formatCurrency(2, pricingInfo.currency) }}
            </div>
          </div>

          <div
            class="columns mt-0 mb-0 is-size-7"
            style="border-bottom: 1px solid #333333"
          >
            <div class="column p-0 has-text-left">Original ticket</div>
            <div class="column p-0 has-text-right is-narrow">
              ({{ pricingInfo.originalTotal | formatCurrency(2, pricingInfo.currency) }})
            </div>
          </div>

          <div
            v-if="pricingInfo.fareDifference"
            class="columns mt-0 mb-0 is-size-7"
          >
            <div class="column p-0 has-text-left">Fare Difference</div>
            <div class="column p-0 has-text-right is-narrow">
              {{ pricingInfo.fareDifference | formatCurrency(2, pricingInfo.currency) }}
            </div>
          </div>

          <div
            v-if="pricingInfo.exchangePenalty > 0"
            class="columns mt-0 mb-2 has-text-grey-light is-size-7"
            style="border-bottom: 1px solid #ddd;"
          >
            <div class="column p-0 has-text-left">Exchange Fee</div>
            <div class="column p-0 has-text-right is-narrow">
              {{ pricingInfo.exchangePenalty | formatCurrency(2, pricingInfo.currency) }}
            </div>
          </div>

          <div class="columns mt-0 mb-0 has-text-weight-bold is-size-7">
            <div class="column p-0 has-text-left">Total Due Today</div>
            <div class="column p-0 has-text-right is-narrow">
              {{ pricingInfo.totalDue | formatCurrency(2, pricingInfo.currency) }}
            </div>
          </div>

          <div class="columns mt-0 mb-0 is-size-7">
            <div class="column p-0 has-text-left">Residual Value</div>
            <div class="column p-0 has-text-right is-narrow">
              {{ pricingInfo.residualValue | formatCurrency(2, pricingInfo.currency) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="has-text-centered pb-5">
        <div class="is-size-5 mb-3">Checking latest fare details...</div>
        <b-loading
          :is-full-page="false"
          :active="true"
          :can-cancel="false"
        ></b-loading>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="mx-auto">
        <b-notification
          type="is-danger"
          :closable="false"
          position="is-top-right"
          has-icon
          icon="alert-circle"
          icon-size="is-small"
        >
          <div class="is-size-5 mt-2">
            Unable to verify pricing
          </div>
          <p class="is-size-7">{{ error }}</p>
        </b-notification>


        <div class="mt-3 mx-auto" style="max-width:fit-content">
          <b-button
            @click="retryPriceCheck"
            type="is-info"
            class="mr-2"
          >
            Retry
          </b-button>
          <b-button @click="reject()" type="is-default">
            Cancel
          </b-button>
        </div>
      </div>

      <!-- Accept Offer -->
      <div v-else>
        <!-- Offer Details -->
        <div class="mt-4">
          <p class="is-size-7 has-text-grey mb-2">
            <b-icon icon="information" size="is-small"></b-icon>
            Offer expires: {{ offerExpireTime }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="mt-5 is-flex is-justify-content-flex-end">
          <b-button @click="reject()" type="is-light" class="mr-2">
            Close
          </b-button>
          <b-button @click="accept()" type="is-primary" :loading="accepting">
            Accept Price
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NDCTag from "@/components/Tags/NDCTag.vue"
import JourneySummaryAlt from "./JourneySummaryAlt.vue"

export default {
  name: "NDCPriceCheckModal",
  components: {
    NDCTag,
    JourneySummaryAlt,
  },
  props: {
    flight: Object,
    offer: Object,
    guest: Object,
  },
  data() {
    return {
      loading: true,
      accepting: false,
      error: null,
      pricedOffer: null,
      originalPrice: 0,
      newPrice: 0,
      pricingInfo: null,
    }
  },
  computed: {
    flightPricing() {
      return this.flight.pricing
    },
    firstJourney() {
      return this.offer.journeyList[0]
    },
    fareDescription() {
      return this.firstJourney.bookingClass
    },
    priceDifference() {
      return this.newPrice - this.originalPrice
    },
    priceChangeType() {
      return this.priceDifference > 0 ? "is-danger" : "is-success"
    },
    priceChangeIcon() {
      return this.priceDifference > 0 ? "arrow-up" : "arrow-down"
    },
    priceChangeMessage() {
      if (this.priceDifference > 0) {
        return "Price Increased"
      } else if (this.priceDifference < 0) {
        return "Price Decreased"
      } else {
        return "Price Unchanged"
      }
    },
    priceChangeDirection() {
      return this.priceDifference > 0 ? "Increase:" : "Decrease:"
    },
    offerExpireTime() {
      if (this.pricedOffer?.expireTime) {
        return new Date(this.pricedOffer.expireTime).toLocaleString()
      }
      return "N/A"
    },
    priceComparisonDifference() {
      // Compare pricingInfo.total (new price) with offer.pricing.total (original offer price)
      if (!this.pricingInfo?.total || !this.offer?.pricing?.total) {
        return 0
      }
      return this.pricingInfo.total - this.offer.pricing.total
    },
    priceComparisonType() {
      // Green (success) if price went down, red (danger) if price went up
      return this.priceComparisonDifference < 0 ? "is-success" : "is-danger"
    },
    priceComparisonIcon() {
      // Down arrow if price decreased, up arrow if price increased
      return this.priceComparisonDifference < 0 ? "arrow-down" : "arrow-up"
    },
  },
  methods: {
    async checkPrice() {
      try {
        this.loading = true
        this.error = null

        const atcDetails = this.$store.state.bookTravel.atc
        if (!atcDetails) {
          throw new Error("ATC booking details not found")
        }

        const body = {
          meta: {
            stateful: false,
            normalizeResponse: true,
          },
          creationOfficeId: atcDetails.creationOfficeId,
          orderId: atcDetails.orderId,
          carrier: atcDetails.ownerCode,
          offerId: this.offer.offerId,
          ownerCode: this.offer.ownerCode,
          responseId: this.offer.responseId,
          items:
            this.offer.items.map((item) => ({
              itemId: item.itemId,
              paxRef: item.paxRef,
            })) || [],
          paxList: atcDetails.ndcInfo?.paxList || [],
        }

        const response = await this.$app.huddleLabsServerCall(
          "amadeus/air/ndc/price-exchange",
          body
        )

        if (response.data) {
          // Extract the priced Offer from the response
          const selectedOffer = response.data.selectedOffer

          // Validate required fields
          if (!selectedOffer?.responseId) {
            throw new Error("Invalid response: Missing responseId")
          }
          if (!selectedOffer?.offerId) {
            throw new Error("Invalid response: Missing offerId")
          }
          if (!selectedOffer?.pricing) {
            throw new Error("Invalid response: Missing pricing information")
          }

          this.pricedOffer = selectedOffer
          this.pricingInfo = selectedOffer.pricing
          this.newPrice = selectedOffer.pricing.total || 0
          this.originalPrice = this.flightPricing?.totalDue || 0
        } else {
          throw new Error("Offer missing or expired. Please try again.")
        }
      } catch (err) {
        this.error = err.message || "Failed to retrieve pricing information"
        console.error("Price check error:", err)
      } finally {
        this.loading = false
      }
    },
    async accept() {
      try {
        this.accepting = true

        if (!this.pricedOffer) {
          throw new Error("No offer data available")
        }

        // Build the offer object with the required fields for setNdcBooking
        const updatedOffer = {
          offerId: this.pricedOffer.offerId,
          ownerCode: this.pricedOffer.ownerCode,
          responseId: this.pricedOffer.responseId,
          expireTime: this.pricedOffer.expireTime,
          items: this.pricedOffer.items,
          pricing: this.pricedOffer.pricing,
          journeyList: this.offer.journeyList,
          bookingClass: this.offer.bookingClass,
        }

        // Update the flight result with new offer details
        const updatedFlight = {
          ...this.flight,
          offer: updatedOffer,
          pricing: this.pricedOffer.pricing,
        }

        let payload = {
          flight: updatedFlight,
          offer: updatedOffer,
        }

        if (this.guest) {
          payload.guestId = this.guest.meetingAttendeeGuestId
        }

        // Dispatch the setNdcBooking action
        await this.$store.dispatch("bookTravel/setNdcBooking", payload)

        // Close the modal and emit accept event
        this.$emit("accept", { flight: updatedFlight, offer: updatedOffer })
        this.$parent.close()
      } catch (err) {
        this.$buefy.toast.open({
          message: "Failed to accept offer",
          type: "is-danger",
        })
        console.error("Accept error:", err)
      } finally {
        this.accepting = false
      }
    },
    reject() {
      this.$emit("reject")
      this.$parent.close()
    },
    retryPriceCheck() {
      this.checkPrice()
    },
  },
  mounted() {
    this.checkPrice()
  },
}
</script>

<style scoped>
.has-background-danger-light {
  background-color: #ffede9;
}

.has-background-success-light {
  background-color: #f0fdf4;
}

.card {
  min-width: 500px;
  min-height: 400px;
}
</style>
